import { useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal } from '@/shared/ui/Modal';
import type { ReferralStatus, ReportInput, StatusChangeInput } from '../api/referralsApi';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './RecipientActions.module.css';

interface RecipientActionsProps {
  status: ReferralStatus;
  onStatus: (input: StatusChangeInput) => Promise<unknown>;
  onReport: (input: ReportInput) => Promise<unknown>;
  onNote: (note: string, actorName?: string) => Promise<unknown>;
  /** Public link viewers have no account; ask who they are once. */
  askActorName?: boolean;
  defaultActorName?: string;
  busy?: boolean;
}

/**
 * What the receiving office can do with a slip: mark it scheduled or completed, decline it, add a
 * note the referring office sees, and send a report back. Same component behind the inbox and the
 * emailed link.
 */
export function RecipientActions({ status, onStatus, onReport, onNote, askActorName = false, defaultActorName = '', busy = false }: RecipientActionsProps) {
  const [modal, setModal] = useState<null | 'schedule' | 'decline' | 'report' | 'note'>(null);
  const [actorName, setActorName] = useState(defaultActorName);
  const [scheduledFor, setScheduledFor] = useState('');
  const [note, setNote] = useState('');
  const [report, setReport] = useState({ body: '', procedures: '', outcome: '', followUp: '' });
  const closed = status === 'completed' || status === 'cancelled';
  const canSchedule = status === 'sent' || status === 'viewed' || status === 'declined' || status === 'scheduled';
  const canComplete = status === 'sent' || status === 'viewed' || status === 'scheduled';
  const canDecline = status === 'sent' || status === 'viewed' || status === 'scheduled';
  const nameOk = !askActorName || actorName.trim().length > 1;

  const reset = () => {
    setModal(null);
    setNote('');
    setScheduledFor('');
    setReport({ body: '', procedures: '', outcome: '', followUp: '' });
  };

  const submit = async () => {
    const who = askActorName ? actorName.trim() : undefined;
    if (modal === 'schedule') {
      await onStatus({ status: 'scheduled', scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null, note: note || null, actorName: who });
    } else if (modal === 'decline') {
      await onStatus({ status: 'declined', note: note || null, actorName: who });
    } else if (modal === 'note') {
      await onNote(note, who);
    } else if (modal === 'report') {
      await onReport({
        authorName: who ?? defaultActorName ?? 'Receiving office',
        body: report.body,
        procedures: report.procedures
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
        outcome: report.outcome || null,
        followUp: report.followUp || null,
      });
    }
    reset();
  };

  if (closed && status === 'cancelled') return null;

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        {canSchedule && (
          <Button size="sm" onClick={() => setModal('schedule')} disabled={busy}>
            {status === 'scheduled' ? 'Reschedule' : 'Mark scheduled'}
          </Button>
        )}
        {canComplete && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void onStatus({ status: 'completed', actorName: askActorName ? actorName || undefined : undefined })}>
            Mark completed
          </Button>
        )}
        {!closed && (
          <Button size="sm" variant="secondary" onClick={() => setModal('report')} disabled={busy}>
            Send report back
          </Button>
        )}
        {status === 'completed' && (
          <Button size="sm" onClick={() => setModal('report')} disabled={busy}>
            Send report back
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setModal('note')} disabled={busy}>
          Add note
        </Button>
        {canDecline && (
          <Button size="sm" variant="ghost" onClick={() => setModal('decline')} disabled={busy} className={styles.decline}>
            Decline
          </Button>
        )}
      </div>

      <Modal
        isOpen={modal !== null}
        onClose={reset}
        size="sm"
        title={
          modal === 'schedule'
            ? 'Mark patient scheduled'
            : modal === 'decline'
              ? 'Decline this referral'
              : modal === 'report'
                ? 'Send a report back'
                : 'Add a note for the referring office'
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={reset}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={modal === 'decline' ? 'destructive' : 'primary'}
              loading={busy}
              disabled={!nameOk || (modal === 'note' && !note.trim()) || (modal === 'report' && !report.body.trim())}
              onClick={() => void submit()}
            >
              {modal === 'schedule' ? 'Save' : modal === 'decline' ? 'Decline referral' : modal === 'report' ? 'Send report' : 'Add note'}
            </Button>
          </>
        }
      >
        <div className={patterns.fieldStack}>
          {askActorName && <Input label="Your name" fullWidth value={actorName} onChange={(e) => setActorName(e.target.value)} placeholder="Dr. A Rega / Front desk" />}
          {modal === 'schedule' && (
            <Input label="Appointment (optional)" type="datetime-local" fullWidth value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} />
          )}
          {(modal === 'schedule' || modal === 'decline' || modal === 'note') && (
            <label className={patterns.field}>
              <span className={patterns.fieldLabel}>{modal === 'decline' ? 'Reason (shared with the referring office)' : 'Note (optional, shared with the referring office)'}</span>
              <textarea className={patterns.textarea} rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
            </label>
          )}
          {modal === 'report' && (
            <>
              <Input label="Procedures (comma-separated)" fullWidth value={report.procedures} onChange={(e) => setReport((r) => ({ ...r, procedures: e.target.value }))} placeholder="Extraction #30, bone graft" />
              <label className={patterns.field}>
                <span className={patterns.fieldLabel}>Report</span>
                <textarea className={patterns.textarea} rows={6} value={report.body} onChange={(e) => setReport((r) => ({ ...r, body: e.target.value }))} maxLength={8000} placeholder="Findings, treatment rendered, anesthesia, complications, post-op instructions…" />
              </label>
              <Input label="Outcome (optional)" fullWidth value={report.outcome} onChange={(e) => setReport((r) => ({ ...r, outcome: e.target.value }))} />
              <Input label="Follow-up for the referring office (optional)" fullWidth value={report.followUp} onChange={(e) => setReport((r) => ({ ...r, followUp: e.target.value }))} placeholder="Restore #30 in 3–4 months" />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
